import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, LoadingSpinner } from '@/components/common';
import api from '@/services/api';
import { formatCurrency } from '@/utils';

/**
 * #703: the Earned / Paid / Payable breakdown behind the two dashboard cards
 * (`commission_summary`, #410). Paid here is commission already paid out
 * (calculations marked paid or carried by a completed payout), Payable is
 * earned minus paid; it is not the "Paid Amount" of the Report tab, which is
 * the payments the insurer recorded against its own schedules.
 */

interface BrokerRow {
  broker_id: string;
  broker_name: string;
  policies: number;
  earned: string;
  paid: string;
  payable: string;
}
interface PolicyRow {
  calculation_id: string;
  policy_id: string;
  policy_number: string;
  broker_name: string;
  policy_date: string | null;
  earned: string;
  paid: string;
  payable: string;
  status: string;
}
interface Detail {
  earned: string;
  paid: string;
  payable: string;
  brokers: BrokerRow[];
  policies: PolicyRow[];
  policy_rows_truncated: boolean;
}

const TH = 'px-3 py-2 text-xs font-semibold uppercase text-gray-600 dark:text-gray-300';
const TD = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100';

export function CommissionLedgerTab() {
  const q = useQuery({
    queryKey: ['insurerCommissionLedger'],
    queryFn: async (): Promise<Detail> =>
      (await api.get('/insurers/insurance/broker-management/commissions/')).data,
  });
  if (q.isLoading) return <LoadingSpinner />;
  if (q.isError || !q.data) {
    return <p className="text-sm text-red-600">Could not load the commission ledger.</p>;
  }
  const d = q.data;
  const kpis: [string, string, string][] = [
    ['Earned', d.earned, 'Commission earned on current calculations for policies on your paper.'],
    [
      'Paid',
      d.paid,
      'Commission already paid out: calculations marked paid or carried by a completed payout.',
    ],
    ['Payable', d.payable, 'Earned minus paid: still to be paid out.'],
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {kpis.map(([label, value, help]) => (
          <Card key={label}>
            <div title={help}>
              <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {formatCurrency(Number(value))}
              </p>
            </div>
          </Card>
        ))}
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Earned = Paid + Payable. These are the dashboard figures. The Report tab&apos;s Paid Amount
        counts the payments you recorded, which is a different measure.
      </p>
      <Card>
        <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">By broker</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className={`${TH} text-left`}>Broker</th>
                <th className={`${TH} text-right`}>Policies</th>
                <th className={`${TH} text-right`}>Earned</th>
                <th className={`${TH} text-right`}>Paid</th>
                <th className={`${TH} text-right`}>Payable</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {d.brokers.map((b) => (
                <tr key={b.broker_id}>
                  <td className={TD}>{b.broker_name}</td>
                  <td className={`${TD} text-right`}>{b.policies}</td>
                  <td className={`${TD} text-right`}>{formatCurrency(Number(b.earned))}</td>
                  <td className={`${TD} text-right`}>{formatCurrency(Number(b.paid))}</td>
                  <td className={`${TD} text-right`}>{formatCurrency(Number(b.payable))}</td>
                </tr>
              ))}
              {d.brokers.length === 0 && (
                <tr>
                  <td className={TD} colSpan={5}>
                    No commission calculated yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      <Card>
        <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">By policy</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className={`${TH} text-left`}>Policy</th>
                <th className={`${TH} text-left`}>Broker</th>
                <th className={`${TH} text-right`}>Earned</th>
                <th className={`${TH} text-right`}>Paid</th>
                <th className={`${TH} text-right`}>Payable</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {d.policies.map((p) => (
                <tr key={p.calculation_id}>
                  <td className={TD}>
                    <Link
                      className="text-primary-600 hover:underline dark:text-primary-400"
                      to={`/policies/${p.policy_id}`}
                    >
                      {p.policy_number}
                    </Link>
                  </td>
                  <td className={TD}>{p.broker_name}</td>
                  <td className={`${TD} text-right`}>{formatCurrency(Number(p.earned))}</td>
                  <td className={`${TD} text-right`}>{formatCurrency(Number(p.paid))}</td>
                  <td className={`${TD} text-right`}>{formatCurrency(Number(p.payable))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {d.policy_rows_truncated && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Showing the newest {d.policies.length} policies. The by-broker table covers all of them.
          </p>
        )}
      </Card>
    </div>
  );
}
